// =====================================================
// SERVIÇO DE TOOLS DO SAIPOS PARA IAs
// Expõe funções do Saipos como tools nativas da OpenAI
// =====================================================

import { saiposService, SaiposHttpError } from './saipos-service';

/** id_sale_type → Tipo de venda (quando o cliente perguntar o tipo da venda, use este campo) */
export const SAIPOS_SALE_TYPE_LABELS: Record<number, string> = {
  1: 'Entrega',
  2: 'Retirada no balcão / Takeout',
  3: 'Salão (mesas e comandas)',
  4: 'Ficha / Senha'
};

/** id_movement_type → Tipo de movimentação de estoque (layout API search_ingredient_movement) */
export const SAIPOS_MOVEMENT_TYPE_LABELS: Record<number, string> = {
  1: 'Saída de venda',
  2: 'Saída manual',
  3: 'Entrada por NF',
  4: 'Entrada manual',
  6: 'Perda',
  7: 'Devolução de fornecedor',
  8: 'Devolução de venda',
  9: 'Entrada por beneficiamento',
  10: 'Saída por beneficiamento',
  11: 'Entrada por ajuste',
  12: 'Saída por ajuste',
  14: 'Saída por transferência',
  15: 'Entrada por transferência',
  48: 'Consumo'
};

/** Prompts exclusivos por operação — instruem o agente como gerar a resposta final com base nos dados */
const TOOL_PROMPTS: Record<string, string> = {
  get_sales: 'Use exatamente os dados abaixo (vendas e payment_amount) para sua resposta. Não invente valores.',
  get_sale_items: 'Use os CÁLCULOS agregados abaixo (quantidade e valor por item já somados). Cite exatamente os valores que aparecem na aba Cálculos. Não recalcule — use os totais hardcoded.',
  get_stock_position: 'Use os dados de ingrediente, grupo, estoque atual e valor unitário abaixo para sua resposta final.',
  get_catalog: 'Use a lista de produtos e preços abaixo para responder ao usuário.'
};

function getSaleTypeLabel(idSaleType: number | string | null | undefined): string {
  if (idSaleType == null) return '';
  const n = typeof idSaleType === 'string' ? parseInt(idSaleType, 10) : idSaleType;
  return SAIPOS_SALE_TYPE_LABELS[n] ?? '';
}

function getMovementTypeLabel(idMovementType: number | string | null | undefined): string {
  if (idMovementType == null) return 'Movimentação';
  const n = typeof idMovementType === 'string' ? parseInt(idMovementType, 10) : idMovementType;
  return SAIPOS_MOVEMENT_TYPE_LABELS[n] ?? `Tipo ${n}`;
}

/** Extrai valor monetário da venda: tenta payment_amount e vários nomes (API pode variar) */
function getSaleAmountFromRecord(sale: any): number {
  const knownKeys = [
    'payment_amount', 'paymentAmount', 'Payment_Amount',
    'total', 'Total', 'valor', 'amount', 'value',
    'total_amount', 'amount_total', 'valor_total', 'valor_pago'
  ];
  for (const key of knownKeys) {
    const raw = sale[key];
    if (raw != null && raw !== '') {
      const n = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : Number(raw);
      if (Number.isFinite(n)) return n;
    }
  }
  // Último recurso: qualquer chave que sugira valor monetário
  if (sale && typeof sale === 'object') {
    for (const key of Object.keys(sale)) {
      const k = key.toLowerCase();
      if ((k.includes('payment') || k.includes('amount') || k.includes('total') || k.includes('valor')) && typeof sale[key] === 'number') {
        return Number(sale[key]);
      }
      if ((k.includes('payment') || k.includes('amount') || k.includes('valor')) && typeof sale[key] === 'string') {
        const n = parseFloat(String(sale[key]).replace(',', '.'));
        if (Number.isFinite(n)) return n;
      }
    }
  }
  return 0;
}

/** Formata resposta do catálogo /v2/catalog para exibição (estrutura pode variar). */
function formatCatalogForDisplay(catalog: any): string {
  if (catalog == null) return 'Sem dados.';
  if (Array.isArray(catalog)) {
    return catalog.slice(0, 100).map((item: any, i: number) => {
      const name = item.name ?? item.title ?? item.desc ?? item.description ?? item.id ?? `Item ${i + 1}`;
      const price = item.price ?? item.valor ?? item.value;
      const line = price != null ? `${i + 1}. ${name} - R$ ${Number(price).toFixed(2)}` : `${i + 1}. ${name}`;
      return line;
    }).join('\n') + (catalog.length > 100 ? `\n... e mais ${catalog.length - 100} itens.` : '');
  }
  if (typeof catalog === 'object') {
    const parts: string[] = [];
    if (catalog.categories && Array.isArray(catalog.categories)) {
      for (const cat of catalog.categories) {
        const catName = cat.name ?? cat.title ?? cat.description ?? 'Categoria';
        parts.push(`\n**${catName}**`);
        const items = cat.items ?? cat.products ?? cat.itens ?? [];
        for (let i = 0; i < Math.min(items.length, 50); i++) {
          const item = items[i];
          const name = item.name ?? item.title ?? item.desc ?? item.description ?? '';
          const price = item.price ?? item.valor ?? item.value;
          parts.push(price != null ? `  • ${name} - R$ ${Number(price).toFixed(2)}` : `  • ${name}`);
        }
        if (items.length > 50) parts.push(`  ... e mais ${items.length - 50} itens.`);
      }
    }
    if (catalog.products && Array.isArray(catalog.products)) {
      parts.push('\n**Produtos**');
      catalog.products.slice(0, 80).forEach((item: any, i: number) => {
        const name = item.name ?? item.title ?? item.desc ?? '';
        const price = item.price ?? item.valor ?? item.value;
        parts.push(price != null ? `  ${i + 1}. ${name} - R$ ${Number(price).toFixed(2)}` : `  ${i + 1}. ${name}`);
      });
      if (catalog.products.length > 80) parts.push(`  ... e mais ${catalog.products.length - 80} itens.`);
    }
    if (parts.length > 0) return parts.join('\n').trim();
    try {
      const str = JSON.stringify(catalog, null, 2);
      return str.length > 3000 ? str.slice(0, 3000) + '\n...' : str;
    } catch {
      return String(catalog);
    }
  }
  return String(catalog);
}

class SaiposToolsService {
  /**
   * Retorna as tools do Saipos para a API da OpenAI
   */
  getSaiposTools() {
    return [
      {
        type: 'function',
        function: {
          name: 'saipos_get_sales',
          description: `Consulta vendas realizadas no Saipos. Use quando o usuário perguntar sobre vendas, faturamento, receita, pedidos concluídos.
IMPORTANTE - Valor/faturamento em reais: use SEMPRE o campo payment_amount de cada venda (não use o campo "total", que pode vir zerado).
Quando o cliente perguntar o TIPO da venda, use id_sale_type: 1=Entrega, 2=Retirada no balcão/Takeout, 3=Salão (mesas e comandas), 4=Ficha/Senha.`,
          parameters: {
            type: 'object',
            properties: {
              store_id: {
                type: 'string',
                description: 'ID da loja (opcional, usa o ID salvo na configuração se não informado)'
              },
              date_from: {
                type: 'string',
                description: 'Data inicial no formato YYYY-MM-DD (ex: 2026-02-01)'
              },
              date_to: {
                type: 'string',
                description: 'Data final no formato YYYY-MM-DD (ex: 2026-02-01)'
              },
              status: {
                type: 'string',
                description: 'Status da venda (opcional)'
              },
              limit: {
                type: 'number',
                description: 'Número máximo de resultados (padrão: 100)'
              }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'saipos_get_sale_items',
          description: 'Consulta os itens/produtos das vendas no Saipos (sales_items). Use quando o usuário perguntar sobre itens vendidos, produtos das vendas, detalhes dos pedidos. No retorno, use sempre: quantity (quantidade), unit_price (valor unitário em R$), total vendido = quantity × unit_price.',
          parameters: {
            type: 'object',
            properties: {
              date_from: {
                type: 'string',
                description: 'Data inicial no formato YYYY-MM-DD (opcional; padrão: início do ano anterior)'
              },
              date_to: {
                type: 'string',
                description: 'Data final no formato YYYY-MM-DD (opcional; padrão: fim do ano atual)'
              },
              date_column_filter: {
                type: 'string',
                description: 'Coluna de data para filtro (opcional; padrão: shift_date)'
              },
              limit: {
                type: 'number',
                description: 'Número máximo de resultados (padrão: 300)'
              },
              offset: {
                type: 'number',
                description: 'Offset para paginação (padrão: 0)'
              }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'saipos_get_stock_position',
          description: 'Consulta a posição atual de estoque de ingredientes no Saipos. Use quando o usuário perguntar sobre estoque disponível, quantidade em estoque, saldo atual. A API aceita no máximo 15 dias; use days: 7 para "última semana".',
          parameters: {
            type: 'object',
            properties: {
              store_id: {
                type: 'string',
                description: 'ID da loja (opcional)'
              },
              ingredient_id: {
                type: 'string',
                description: 'ID do ingrediente específico (opcional)'
              },
              days: {
                type: 'number',
                description: 'Últimos N dias para considerar (1 a 15; padrão 15). Use 7 para "última semana".'
              }
            },
            required: []
          }
        }
      },
      {
        type: 'function',
        function: {
          name: 'saipos_get_catalog',
          description: 'Consulta o cardápio/catálogo do Saipos (produtos, categorias, itens disponíveis). Use quando o usuário perguntar sobre cardápio, menu, catálogo, produtos à venda.',
          parameters: {
            type: 'object',
            properties: {},
            required: []
          }
        }
      }
    ];
  }

  /**
   * Executa uma operação do Saipos
   */
  async executeSaiposTool(
    functionName: string, 
    args: any, 
    userId: string
  ): Promise<{ success: boolean; result?: any; error?: string; formattedResponse: string; httpStatus?: number }> {
    try {
      console.log(`🍽️ Executando ${functionName} com args:`, args);

      if (functionName === 'saipos_get_sales') {
        const { data: sales, status } = await saiposService.getSales(userId, {
          date_from: args.date_from,
          date_to: args.date_to,
          limit: args.limit,
          offset: args.offset
        });

        if (!sales || sales.length === 0) {
          return {
            success: true,
            result: [],
            formattedResponse: '📊 Nenhuma venda encontrada no período solicitado.',
            httpStatus: status
          };
        }

        // Valor da venda: tenta payment_amount e vários nomes (API Saipos pode variar)
        const getSaleAmount = (sale: any) => getSaleAmountFromRecord(sale);

        // Formatar as vendas de forma legível (incluir tipo da venda via id_sale_type)
        const formattedSales = sales.map((sale: any, index: number) => {
          const date = sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('pt-BR') : 'Data não informada';
          const amount = getSaleAmount(sale);
          const valueStr = amount > 0 ? `R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'Valor não informado';
          const typeLabel = getSaleTypeLabel(sale.id_sale_type);
          const typeStr = typeLabel ? ` - Tipo: ${typeLabel}` : '';
          return `${index + 1}. Venda #${sale.sale_number || sale.id || 'N/A'} - ${date} - ${valueStr}${typeStr}`;
        }).join('\n');

        const totalValue = sales.reduce((sum: number, sale: any) => sum + getSaleAmount(sale), 0);

        // Enviar resultado com valor em reais explícito (payment_amount) para a IA usar em faturamento
        const resultForAi = sales.map((sale: any) => ({
          ...sale,
          valor_reais: getSaleAmount(sale)
        }));

        return {
          success: true,
          result: resultForAi,
          formattedResponse: `📋 **PROMPT:** ${TOOL_PROMPTS.get_sales}

📊 **Vendas encontradas (${sales.length}):**\nOs valores em R$ abaixo são do campo **payment_amount** (use-o para faturamento em reais).\n\n${formattedSales}\n\n💰 **Total faturamento (payment_amount): R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}**`,
          httpStatus: status
        };
      }

      if (functionName === 'saipos_get_sale_items') {
        const { data: items, status } = await saiposService.getSaleItems(userId, {
          date_from: args.date_from,
          date_to: args.date_to,
          date_column_filter: args.date_column_filter,
          limit: args.limit,
          offset: args.offset
        });

        if (!items || items.length === 0) {
          const calculosVazio = 'Itens agregados por desc_sale_item_choice:\n\nNenhum item no período.\n\nTotal geral: R$ 0,00';
          return {
            success: true,
            result: {
              response: [],
              calculos: calculosVazio
            },
            formattedResponse: `📋 **PROMPT:** ${TOOL_PROMPTS.get_sale_items}

📦 Nenhum item de venda encontrado no período. Informe isso ao usuário.`,
            httpStatus: status
          };
        }

        // Achatar: se items são vendas com sale_items/items/itens aninhados, extrair os sub-itens
        const flatItems: any[] = [];
        for (const row of items) {
          const subArr = row?.sale_items ?? row?.sale_items_detail ?? row?.items ?? row?.itens ?? row?.item_list;
          if (Array.isArray(subArr) && subArr.length > 0) {
            flatItems.push(...subArr);
          } else {
            flatItems.push(row);
          }
        }
        const itemsToAggregate = flatItems.length > 0 ? flatItems : items;

        // Coleta todos objetos aninhados (até 2 níveis) para busca
        const collectAllObjects = (it: any, depth = 0): any[] => {
          if (!it || depth > 2) return [];
          const out: any[] = [it];
          if (typeof it === 'object') {
            for (const key of Object.keys(it)) {
              const v = it[key];
              if (v && typeof v === 'object' && !Array.isArray(v) && v !== it) {
                out.push(...collectAllObjects(v, depth + 1));
              }
            }
          }
          return out;
        };
        const parseNum = (v: any): number | null => {
          if (v == null || v === '') return null;
          const n = typeof v === 'string' ? Number(String(v).replace(',', '.')) : Number(v);
          return Number.isFinite(n) ? n : null;
        };
        const getQuantity = (it: any): number => {
          const keysQty = ['quantity', 'quantidade', 'qty', 'qtd', 'amount', 'quantity_item', 'num_quantity', 'qty_sold', 'quant', 'num_item', 'qtd_item', 'quantidade_item'];
          for (const obj of collectAllObjects(it)) {
            for (const k of keysQty) {
              const n = parseNum(obj?.[k]);
              if (n != null && n >= 0) return n;
            }
            for (const key of Object.keys(obj || {})) {
              const lower = key.toLowerCase();
              if ((lower.includes('quant') || lower.includes('qty') || lower.includes('qtd') || lower.includes('amount')) && !lower.includes('total')) {
                const n = parseNum(obj[key]);
                if (n != null && n >= 0) return n;
              }
            }
          }
          return 0;
        };
        const getUnitPrice = (it: any): number | null => {
          const keysPrice = ['unit_price', 'valor', 'price', 'preco', 'unit_price_item', 'valor_unitario', 'preco_unitario', 'price_unit', 'valor_item', 'value'];
          for (const obj of collectAllObjects(it)) {
            for (const k of keysPrice) {
              const n = parseNum(obj?.[k]);
              if (n != null) return n;
            }
            for (const key of Object.keys(obj || {})) {
              const lower = key.toLowerCase();
              if ((lower.includes('price') || lower.includes('valor') || lower.includes('preco') || lower === 'value') && !lower.includes('total'))
                return parseNum(obj[key]) ?? null;
            }
          }
          return null;
        };
        const getTotal = (it: any): number | null => {
          const keysTotal = ['total', 'total_item', 'valor_total', 'amount_total', 'total_value', 'total_amount', 'total_item_price', 'valor_total_item', 'payment_amount', 'valor_pago'];
          for (const obj of collectAllObjects(it)) {
            for (const k of keysTotal) {
              const n = parseNum(obj?.[k]);
              if (n != null && n >= 0) return n;
            }
            for (const key of Object.keys(obj || {})) {
              const lower = key.toLowerCase();
              if (lower.includes('total') || lower.includes('valor_total') || lower.includes('amount_total')) {
                const n = parseNum(obj[key]);
                if (n != null && n >= 0) return n;
              }
            }
          }
          return null;
        };
        // Fallback: qualquer valor monetário positivo (campo numérico que pareça preço/valor)
        const getAnyValue = (it: any): number | null => {
          const objs = collectAllObjects(it);
          const candidates: number[] = [];
          for (const obj of objs) {
            for (const key of Object.keys(obj || {})) {
              const v = obj[key];
              const n = parseNum(v);
              if (n != null && n > 0 && n < 50000) {
                const lower = key.toLowerCase();
                if (lower.includes('price') || lower.includes('valor') || lower.includes('preco') || lower.includes('total') || lower.includes('amount') || lower.includes('payment') || lower === 'value')
                  candidates.push(n);
              }
            }
          }
          return candidates.length > 0 ? candidates[0] : null;
        };

        // Agregação hardcoded: somar quantidade e valor por desc_sale_item_choice (sem depender de agentes)
        // Prioridade: 1) total explícito  2) quantity × unit_price  3) unit_price (API usa como valor total)
        const agregadoPorItem = new Map<string, { totalVendido: number; quantity: number }>();
        for (const item of itemsToAggregate) {
          const desc = String(
            (item.desc_sale_item_choice ?? (item.sale_item?.desc_sale_item_choice) ?? item.desc_sale_item ?? (item.sale_item?.desc_sale_item) ?? item.product_name ?? item.productName ?? item.desc_item ?? item.item ?? item.notes ?? 'Item') || 'Sem nome'
          ).trim().slice(0, 200);
          const quantity = getQuantity(item);
          const unit_price = getUnitPrice(item);
          const totalApi = getTotal(item);
          let totalVendido: number;
          if (totalApi != null && Number.isFinite(totalApi)) {
            totalVendido = totalApi;
          } else if (quantity > 0 && unit_price != null && Number.isFinite(unit_price)) {
            totalVendido = quantity * unit_price;
          } else if (unit_price != null && Number.isFinite(unit_price)) {
            totalVendido = unit_price;
          } else {
            const anyVal = getAnyValue(item);
            totalVendido = anyVal != null ? anyVal : 0;
          }
          const qtyToAdd = quantity > 0 ? quantity : (totalVendido > 0 ? 1 : 0);
          const key = desc || 'Sem nome';
          const atual = agregadoPorItem.get(key) ?? { totalVendido: 0, quantity: 0 };
          agregadoPorItem.set(key, {
            totalVendido: atual.totalVendido + totalVendido,
            quantity: atual.quantity + qtyToAdd
          });
        }
        const aggregatedList = Array.from(agregadoPorItem.entries())
          .sort((a, b) => b[1].totalVendido - a[1].totalVendido)
          .map(([nome, dados], i) => `${i + 1}. ${nome} - quantity: ${dados.quantity} - total vendido: R$ ${dados.totalVendido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)
          .join('\n');
        const totalGeral = Array.from(agregadoPorItem.values()).reduce((s, d) => s + d.totalVendido, 0);

        const allZeros = totalGeral === 0 && itemsToAggregate.length > 0;
        if (allZeros) {
          console.warn('🍽️ [Saipos] sales_items: todos zerados. Amostra 1º item:', JSON.stringify(itemsToAggregate[0]));
          console.warn('🍽️ [Saipos] Chaves do 1º item:', itemsToAggregate[0] ? Object.keys(itemsToAggregate[0]) : []);
        }
        const calculosHeader = allZeros
          ? `(DEBUG: Estrutura do 1º item - chaves: ${itemsToAggregate[0] ? Object.keys(itemsToAggregate[0]).join(', ') : 'N/A'})\n\n`
          : '';
        const calculosText = `${calculosHeader}Itens agregados por desc_sale_item_choice:\n\n${aggregatedList}\n\nTotal geral: R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
        return {
          success: true,
          result: {
            response: items,
            calculos: calculosText
          },
          formattedResponse: `📋 **PROMPT:** ${TOOL_PROMPTS.get_sale_items}

📦 **Itens de venda (agregados por desc_sale_item_choice):**\n\n${aggregatedList}\n\n💰 **Total geral: R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}**`,
          httpStatus: status
        };
      }

      if (functionName === 'saipos_get_stock_position') {
        const { data: positions, status } = await saiposService.getStockPosition(userId, {
          store_id: args.store_id,
          ingredient_id: args.ingredient_id,
          days: args.days
        });

        if (!positions || positions.length === 0) {
          return {
            success: true,
            result: [],
            formattedResponse: '📦 Nenhuma posição de estoque encontrada.',
            httpStatus: status
          };
        }

        const formattedPositions = positions.map((pos: any, index: number) => {
          const desc_store_ingredient = pos.ingredient?.desc_store_ingredient ?? pos.desc_store_ingredient ?? 'Ingrediente sem nome';
          const desc_store_group_ingredient = pos.group?.desc_store_group_ingredient ?? pos.desc_store_group_ingredient ?? '';
          const current_inventory = pos.current_inventory ?? pos.current_stock ?? 0;
          const average_cost = pos.ingredient?.average_cost ?? pos.average_cost;
          const unit = pos.ingredient?.short_desc_unit_measure ?? pos.short_desc_unit_measure ?? pos.unit ?? '';
          const avgStr = average_cost != null && Number.isFinite(average_cost) ? `R$ ${Number(average_cost).toFixed(2)}` : '—';
          const grupoStr = desc_store_group_ingredient ? ` | Grupo: ${desc_store_group_ingredient}` : '';
          return `${index + 1}. Ingrediente: ${desc_store_ingredient}${grupoStr} | Estoque atual: ${current_inventory} ${unit} | Valor unitário: ${avgStr}`;
        }).join('\n');

        return {
          success: true,
          result: positions,
          formattedResponse: `📋 **PROMPT:** ${TOOL_PROMPTS.get_stock_position}

📦 **Posição de estoque (${positions.length}):**\nCampos: **Ingrediente** (desc_store_ingredient), **Grupo de ingrediente** (desc_store_group_ingredient), **Estoque atual** (current_inventory), **Valor unitário** (average_cost).\n\n${formattedPositions}`,
          httpStatus: status
        };
      }

      if (functionName === 'saipos_get_catalog') {
        const { data: catalog, status } = await saiposService.getCatalog(userId);
        if (!catalog) {
          return {
            success: true,
            result: null,
            formattedResponse: '📋 Nenhum dado de cardápio retornado.',
            httpStatus: status
          };
        }
        const formatted = formatCatalogForDisplay(catalog);
        return {
          success: true,
          result: catalog,
          formattedResponse: `📋 **PROMPT:** ${TOOL_PROMPTS.get_catalog}

📋 **Cardápio / Catálogo:**\n\n${formatted}`,
          httpStatus: status
        };
      }

      return {
        success: false,
        error: `Função desconhecida: ${functionName}`,
        formattedResponse: `❌ Erro: função ${functionName} não reconhecida.`
      };

    } catch (error) {
      console.error(`❌ Erro ao executar ${functionName}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      const httpStatus = error instanceof SaiposHttpError ? error.httpStatus : undefined;
      return {
        success: false,
        error: errorMessage,
        formattedResponse: `❌ Erro ao executar ${functionName}: ${errorMessage}`,
        httpStatus
      };
    }
  }
}

export const saiposToolsService = new SaiposToolsService();

